import { CartErrorEvent, CartLinesUpdateEvent } from "@shopify/events";

class CppInClinicGiftCard extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";

    this.form = this.querySelector("form");
    this.panels = [...this.querySelectorAll("[data-panel]")];
    this.steps = this.panels.map((panel) => panel.dataset.panel);
    this.currentStep = this.steps[0];
    this.quantity = 1;
    this.deliveryMethod = "Digital";
    this.submitting = false;

    if (!this.form || this.steps.length === 0) return;

    this.addEventListener("click", (event) => this.handleClick(event));
    this.addEventListener("input", (event) => this.handleInput(event));
    this.form.addEventListener("submit", (event) => this.handleSubmit(event), true);

    this.selectRecipient("A Friend");
    this.selectDelivery("Digital");
    this.selectInitialDesign();
    this.selectInitialAmount();
    this.syncLocations();
    this.showStep(this.currentStep, false);
    this.updateSummary();
  }

  handleClick(event) {
    const button = event.target.closest("button");
    if (!button || !this.contains(button)) return;

    if (button.dataset.recipientChoice) {
      this.selectRecipient(button.dataset.recipientChoice);
      return;
    }

    if (button.dataset.deliveryChoice) {
      this.selectDelivery(button.dataset.deliveryChoice);
      return;
    }

    if (button.hasAttribute("data-design-choice")) {
      this.selectDesign(button);
      return;
    }

    if (button.hasAttribute("data-amount-choice")) {
      this.selectAmount(button);
      return;
    }

    switch (button.dataset.action) {
      case "next":
        this.goNext();
        break;
      case "back":
        this.goBack();
        break;
      case "increase-quantity":
        this.setQuantity(this.quantity + 1);
        break;
      case "decrease-quantity":
        this.setQuantity(Math.max(1, this.quantity - 1));
        break;
    }
  }

  handleInput(event) {
    if (event.target.matches("[data-location-choice]")) {
      this.querySelector("[data-location-group]")?.removeAttribute("aria-invalid");
      this.syncLocations();
    }

    if (event.target.matches("[data-message]")) {
      const count = this.querySelector("[data-message-count]");
      if (count) count.textContent = String(event.target.value.length);
    }

    event.target.removeAttribute("aria-invalid");
    this.clearError();
    this.updateSummary();
  }

  handleSubmit(event) {
    this.syncLocations();
    const invalidStep = this.steps.find((step) => !this.validateStep(step, false));
    if (!invalidStep) {
      this.updateSummary();
      if (this.deliveryMethod === "Physical") {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        void this.submitPhysicalGiftCard();
      }
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.showStep(invalidStep);
    this.validateStep(invalidStep, true);
  }

  selectDelivery(method) {
    if (method === "Physical" && this.dataset.physicalCardEnabled !== "true") return;

    this.deliveryMethod = method === "Physical" ? "Physical" : "Digital";
    this.querySelectorAll("[data-delivery-choice]").forEach((button) => {
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.deliveryChoice === this.deliveryMethod),
      );
    });

    const selected = this.querySelector(`[data-delivery-choice="${this.deliveryMethod}"]`);
    const deliveryProperty = this.querySelector("[data-delivery-method-property]");
    if (deliveryProperty) {
      deliveryProperty.value =
        selected?.dataset.deliveryLabelValue ||
        (this.deliveryMethod === "Physical" ? "Physical gift card" : "Digital gift card");
    }

    const physicalParentProperty = this.querySelector("[data-physical-parent-property]");
    if (physicalParentProperty)
      physicalParentProperty.disabled = this.deliveryMethod !== "Physical";

    this.syncDeliveryFields();
    this.clearError();
    this.updateSummary();
  }

  selectRecipient(type) {
    const typeInput = this.querySelector("[data-recipient-type]");
    if (typeInput) typeInput.value = type;

    this.querySelectorAll("[data-recipient-choice]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.recipientChoice === type));
    });

    this.querySelectorAll("[data-recipient-fields]").forEach((group) => {
      const active = group.dataset.recipientFields === type;
      group.hidden = !active;
      group.querySelectorAll("input").forEach((input) => {
        input.disabled = !active;
        if (!active) input.removeAttribute("aria-invalid");
      });
    });

    this.syncDeliveryFields();

    this.clearError();
    this.updateSummary();
  }

  syncDeliveryFields() {
    const physical = this.deliveryMethod === "Physical";
    const recipientType = this.querySelector("[data-recipient-type]")?.value || "A Friend";

    this.querySelectorAll("[data-digital-recipient-field]").forEach((input) => {
      const active = !physical && recipientType === "A Friend";
      input.disabled = !active;
      input.closest("label")?.toggleAttribute("hidden", !active);
      if (!active) input.removeAttribute("aria-invalid");
    });

    const addressGroup = this.querySelector("[data-physical-recipient-fields]");
    if (addressGroup) {
      const active = physical && recipientType === "A Friend";
      addressGroup.hidden = !active;
      addressGroup.querySelectorAll("input").forEach((input) => {
        input.disabled = !active;
        if (!active) input.removeAttribute("aria-invalid");
      });
    }

    const selfAddressGroup = this.querySelector("[data-physical-self-fields]");
    if (selfAddressGroup) {
      const active = physical && recipientType === "For Me";
      selfAddressGroup.hidden = !active;
      selfAddressGroup.querySelectorAll("input").forEach((input) => {
        input.disabled = !active;
        if (!active) input.removeAttribute("aria-invalid");
      });
    }

    const digitalDeliveryField = this.querySelector("[data-digital-delivery-field]");
    if (digitalDeliveryField) {
      digitalDeliveryField.hidden = physical;
      digitalDeliveryField.querySelectorAll("input").forEach((input) => {
        input.disabled = physical;
        if (physical) input.removeAttribute("aria-invalid");
      });
    }
  }

  selectInitialDesign() {
    const selected =
      this.querySelector('[data-design-choice][aria-pressed="true"]') ||
      this.querySelector("[data-design-choice]");
    if (selected) this.selectDesign(selected);
  }

  selectDesign(selected) {
    this.querySelectorAll("[data-design-choice]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button === selected));
    });

    const labelInput = this.querySelector("[data-design-label]");
    const imageInput = this.querySelector("[data-design-image]");
    if (labelInput) labelInput.value = selected.dataset.designLabelValue || "";
    if (imageInput) imageInput.value = selected.dataset.designImageValue || "";

    this.clearError();
    this.updateSummary();
  }

  selectInitialAmount() {
    const selected =
      this.querySelector('[data-amount-choice][aria-pressed="true"]:not(:disabled)') ||
      this.querySelector("[data-amount-choice]:not(:disabled)");
    if (selected) this.selectAmount(selected);
  }

  selectAmount(selected) {
    this.querySelectorAll("[data-amount-choice]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button === selected));
    });

    const variantId = this.querySelector("[data-variant-id]");
    const variantTitle = this.querySelector("[data-variant-title]");
    const variantPrice = this.querySelector("[data-variant-price]");
    if (variantId) variantId.value = selected.dataset.variantIdValue || "";
    if (variantTitle) variantTitle.value = selected.dataset.variantTitleValue || "";
    if (variantPrice) variantPrice.value = selected.dataset.variantPriceValue || "";

    this.clearError();
    this.updateSummary();
  }

  setQuantity(quantity) {
    this.quantity = quantity;
    const input = this.querySelector("[data-quantity-input]");
    const output = this.querySelector("[data-quantity-output]");
    if (input) input.value = String(quantity);
    if (output) output.textContent = String(quantity);
    this.updateSummary();
  }

  goNext() {
    if (!this.validateStep(this.currentStep, true)) return;
    const index = this.steps.indexOf(this.currentStep);
    const next = this.steps[index + 1];
    if (next) this.showStep(next);
  }

  goBack() {
    const index = this.steps.indexOf(this.currentStep);
    const previous = this.steps[index - 1];
    if (previous) this.showStep(previous);
  }

  showStep(step, moveFocus = true) {
    if (!this.steps.includes(step)) return;
    this.currentStep = step;

    this.panels.forEach((panel) => {
      const active = panel.dataset.panel === step;
      panel.hidden = !active;
      panel.toggleAttribute("data-active", active);
    });

    const activeIndex = this.steps.indexOf(step);
    this.querySelectorAll("[data-progress]").forEach((item, index) => {
      const active = index === activeIndex;
      item.toggleAttribute("aria-current", active);
      item.toggleAttribute("data-complete", index < activeIndex);
    });

    this.clearError();
    this.updateSummary();

    if (moveFocus) {
      const heading = this.querySelector(`[data-panel="${step}"] h3`);
      heading?.focus({ preventScroll: true });
      this.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  validateStep(step, showError) {
    const panel = this.querySelector(`[data-panel="${step}"]`);
    if (!panel) return true;

    if (step === "design" && !this.querySelector('[data-design-choice][aria-pressed="true"]')) {
      return this.failValidation("Please choose a gift card design.", null, showError);
    }

    if (
      step === "amount" &&
      !this.querySelector('[data-amount-choice][aria-pressed="true"]:not(:disabled)')
    ) {
      return this.failValidation("Please choose an available amount.", null, showError);
    }

    const requiredLocationGroup = panel.querySelector(
      "[data-location-group][data-required-locations]",
    );
    if (
      requiredLocationGroup &&
      !requiredLocationGroup.querySelector("[data-location-choice]:checked")
    ) {
      if (showError) requiredLocationGroup.setAttribute("aria-invalid", "true");
      return this.failValidation(
        "Please choose at least one clinic location.",
        requiredLocationGroup.querySelector("[data-location-choice]"),
        showError,
      );
    }

    const requiredInputs = [...panel.querySelectorAll("[data-required]:not(:disabled)")];
    for (const input of requiredInputs) {
      if (!input.value.trim()) {
        return this.failValidation("Please complete all required fields.", input, showError);
      }

      if (input.type === "email" && !input.validity.valid) {
        return this.failValidation("Please enter a valid email address.", input, showError);
      }

      if (input.type === "date" && input.min && input.value < input.min) {
        return this.failValidation(
          `Please choose a delivery date on or after ${this.formatDate(input.min)}.`,
          input,
          showError,
        );
      }
    }

    return true;
  }

  failValidation(message, input, showError) {
    if (showError) {
      this.showError(message);
      if (input) {
        input.setAttribute("aria-invalid", "true");
        input.focus();
      }
    }
    return false;
  }

  showError(message) {
    const error = this.querySelector("[data-error]");
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  clearError() {
    const error = this.querySelector("[data-error]");
    if (!error) return;
    error.textContent = "";
    error.hidden = true;
  }

  async submitPhysicalGiftCard() {
    if (this.submitting) return;

    const parentVariantId = Number(this.querySelector("[data-variant-id]")?.value);
    const addOnVariantId = Number(this.dataset.physicalCardVariantId);
    if (!parentVariantId || !addOnVariantId) {
      this.showError("The physical gift card option is not currently available.");
      return;
    }

    this.submitting = true;
    const addToCart = this.querySelector("add-to-cart-component");
    addToCart?.disable?.();

    const quantity = Number(this.querySelector("[data-quantity-input]")?.value) || 1;
    const properties = this.getLineItemProperties();
    const sectionIds = [...document.querySelectorAll("cart-items-component[data-section-id]")]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
      .slice(0, 5);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: "add",
        context: "product",
        lines: [
          { merchandiseId: String(parentVariantId), quantity },
          { merchandiseId: String(addOnVariantId), quantity },
        ],
        promise: deferred.promise,
      }),
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [
            { id: parentVariantId, quantity, properties },
            {
              id: addOnVariantId,
              quantity,
              parent_id: parentVariantId,
              properties: { _cpp_gift_card_add_on: "true" },
            },
          ],
          sections: sectionIds.join(","),
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(
          result.description || result.message || "Unable to add the gift card to the cart.",
        );
      }

      const cart = await this.fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: "cpp-in-clinic-gift-card",
          sourceId: this.form.id,
          itemCount: quantity * 2,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });

      const liveRegion = this.querySelector('[ref="liveRegion"]');
      if (liveRegion) liveRegion.textContent = Theme.translations.added || "Added to cart";
      addToCart?.animateAddToCart?.();
      this.clearError();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to add the gift card to the cart.";
      this.showError(message);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: "INVALID",
        }),
      );
    } finally {
      this.submitting = false;
      addToCart?.enable?.();
    }
  }

  getLineItemProperties() {
    const properties = {};
    const formData = new FormData(this.form);
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)]$/);
      if (match && String(value).trim()) properties[match[1]] = String(value);
    }
    return properties;
  }

  async fetchCart() {
    const cartItems = document.querySelector("cart-items-component");
    if (cartItems) {
      await customElements.whenDefined("cart-items-component");
      if (typeof cartItems.fetchCartData === "function") return cartItems.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url || "/cart"}.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("The cart could not be refreshed.");
    return response.json();
  }

  updateSummary() {
    const recipientType = this.querySelector("[data-recipient-type]")?.value;
    const activeGroup = this.querySelector(`[data-recipient-fields="${recipientType}"]`);
    const firstName = activeGroup?.querySelector('input[name*="first name"]')?.value.trim() || "";
    const lastName = activeGroup?.querySelector('input[name*="last name"]')?.value.trim() || "";
    const recipient = [firstName, lastName].filter(Boolean).join(" ") || "—";

    this.setText("[data-summary-recipient]", recipient);
    const deliveryLabel = this.querySelector("[data-delivery-method-property]")?.value || "—";
    this.setText("[data-summary-delivery-method]", deliveryLabel);

    const shippingRow = this.querySelector("[data-summary-shipping-row]");
    if (shippingRow) shippingRow.hidden = this.deliveryMethod !== "Physical";
    if (this.deliveryMethod === "Physical") {
      const propertyPrefix = recipientType === "For Me" ? "Buyer" : "Recipient";
      const shipping =
        [
          this.querySelector(`input[name="properties[${propertyPrefix} address]"]`)?.value,
          this.querySelector(`input[name="properties[${propertyPrefix} city]"]`)?.value,
          this.querySelector(`input[name="properties[${propertyPrefix} state/province]"]`)?.value,
          this.querySelector(`input[name="properties[${propertyPrefix} postal code]"]`)?.value,
        ]
          .map((value) => value?.trim())
          .filter(Boolean)
          .join(", ") || "—";
      this.setText("[data-summary-shipping]", shipping);
    }
    this.setText("[data-summary-locations]", this.syncLocations().join(", ") || "—");
    this.setText("[data-summary-design]", this.querySelector("[data-design-label]")?.value || "—");
    this.setText("[data-summary-amount]", this.querySelector("[data-variant-price]")?.value || "—");
    this.setText("[data-summary-quantity]", String(this.quantity));
  }

  syncLocations() {
    const propertyInput = this.querySelector("[data-location-property]");
    if (!propertyInput) return [];

    const locations = [...this.querySelectorAll("[data-location-choice]:checked")]
      .map((input) => input.value.trim())
      .filter(Boolean);
    propertyInput.value = locations.join(", ");
    propertyInput.disabled = locations.length === 0;
    return locations;
  }

  setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }

  formatDate(value) {
    const date = new Date(`${value}T00:00:00`);
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  }
}

if (!customElements.get("cpp-in-clinic-gift-card")) {
  customElements.define("cpp-in-clinic-gift-card", CppInClinicGiftCard);
}
